import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { OpenFeatureProvider } from "@openfeature/react-sdk";
import { Toaster } from "sonner";
import { installErrorToastDuration } from "@/lib/toast";
import { initI18n, i18n, I18nProvider } from "./lib/i18n";
import { initOpenFeature } from "./lib/openfeature";
import { routeTree } from "./routeTree.gen";
import { processOAuthRedirect } from "./hooks/useOAuthRedirect";
import { GlobalDebugDialog } from "@/components/common/GlobalDebugDialog";
import { useGlobalDebugError } from "@/lib/hooks/useGlobalDebugError";

// Process OAuth tokens from URL hash before React renders (Google SSO redirect)
processOAuthRedirect();

// One-time eviction of the retired service-worker `api-cache` bucket. Workbox
// leaves runtime caches behind when their route is removed, so already-installed
// clients would keep serving up-to-24h-old API responses until it is deleted.
if (typeof caches !== "undefined") {
  void caches.delete("api-cache").catch(() => {});
}

// Initialize OpenFeature with Flipt provider
initOpenFeature();

// Create router
const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

const root = createRoot(rootElement);

function GlobalDebugDialogWrapper() {
  const { error, clear } = useGlobalDebugError();
  return <GlobalDebugDialog error={error} onClose={clear} />;
}

async function bootstrap() {
  // Initialize i18n before the first render so the app does not paint in the
  // default language and then depend on a follow-up change event.
  try {
    await initI18n();
  } catch (error) {
    console.error("Failed to initialize i18n:", error);
  }

  installErrorToastDuration();

  root.render(
    <StrictMode>
      <OpenFeatureProvider>
        <RouterProvider router={router} />
        <Toaster position="top-right" closeButton />
        <I18nProvider i18n={i18n}>
          <GlobalDebugDialogWrapper />
        </I18nProvider>
      </OpenFeatureProvider>
    </StrictMode>,
  );
}

void bootstrap().catch((error) => {
  console.error("Failed to bootstrap app:", error);
});
